import { Row, Col, Card } from 'react-bootstrap';
import Section from '../components/Section';
import ServiceCard from '../components/ServiceCard';
import Seo from '../components/Seo';

const Services = () => {
  const serviceQuickLinks = [
    { label: 'Fractional', targetId: 'fractional-services' },
    { label: 'Interim', targetId: 'interim-services' },
    { label: 'Project-Based', targetId: 'project-based-services' },
  ];

  const scrollToSection = (id) => {
    const target = document.getElementById(id);
    if (target) {
      const header = document.querySelector('.site-header');
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const targetTop = target.getBoundingClientRect().top + window.scrollY;
      const offsetTop = Math.max(targetTop - headerHeight, 0);

      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Seo
        title="Services | Fractional Sales VP for Industrial B2B Companies"
        description="SalesFractionPro services: audits, strategy, coaching, recruitment, pricing, and commercial execution for industrial B2B companies."
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Fractional Sales and Commercial Leadership Services',
          provider: {
            '@type': 'Organization',
            name: 'SalesFractionPro',
          },
          serviceType: 'Fractional Sales VP, Interim Commercial Leadership, and Project-Based Sales Support',
          areaServed: ['North America', 'Europe'],
        }}
      />

      <Section className="page-hero" variant="hero">
        <Row>
          <Col>
            <h1 style={{ fontSize: 60 }}>Services</h1>
          </Col>
        </Row>
      </Section>

      <Section className="pb-3">
        <Row className="g-3">
          {serviceQuickLinks.map((item) => (
            <Col xs={12} md={4} key={item.targetId}>
              <Card
                className="service-card service-quick-link-card h-100 p-2"
                role="button"
                tabIndex={0}
                onClick={() => scrollToSection(item.targetId)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    scrollToSection(item.targetId);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <Card.Body className="d-flex align-items-center justify-content-center">
                  <h5 className="mb-0">{item.label}</h5>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Section>

      <Section id='fractional-services' variant='alt' className='pb-0 pt-5'>
        <Row>
          <Col>
            <h2 className='fw-semibold mb-0'>1. Fractional Sales and Commercial VP Assignments</h2>
            <p className='lead'>
              Part-time senior leadership (for example, one day per week for three to twelve months)
            </p>
          </Col>
        </Row>
        <Row className="g-4">
          <Col xs={12}>
            <Card className="service-card h-100 p-2">
              <Card.Body>
                <ul className='mb-0'>
                  <li>Day-to-day sales and commercial leadership</li>
                  <li>Strategy development and execution</li>
                  <li>Sales process and CRM discipline</li>
                  <li>Pricing management and margin improvement</li>
                  <li>Team capability development and coaching</li>
                  <li>Sales incentive and bonus structures</li>
                  <li>Sales team recruitment</li>
                  <li>Building a culture of commercial excellence</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Section>
      <Section id='interim-services' variant='alt' className='pb-0 pt-5'>
        <Row>
          <Col>
            <h2 className='fw-semibold mb-0'>
              2. Full-Time Leadership During Transition or Change
            </h2>
            <p className='lead'>
              Interim sales and commercial VP assignments
            </p>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Card className="service-card h-100 p-2">
              <Card.Body>
                <ul className='mb-0'>
                  <li>Immediate leadership continuity</li>
                  <li>Stabilization, turnaround, or scale-up support</li>
                  <li>Hands-on execution across sales, pricing, and forecasting</li>
                  <li>Team leadership and performance management</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Section>

      <Section id='project-based-services' variant='alt' className='pt-5 pb-6'>
        <Row>
          <Col>
            <h2 className='fw-semibold mb-0'>3. Project-Based Engagements</h2>
            <p className='lead'>
              Focused interventions with clear scope and deliverables
            </p>
          </Col>
        </Row>
        <Row className="g-4 align-items-start">
          <Col xs={12}>
            <ServiceCard
              title="Sales Process Audit"
              body="I assess your end-to-end sales process against best practices to identify gaps, inefficiencies, and improvement opportunities."
              detailsTitle="View detailed scope"
              details={
                <>
                  <h5>Focus Areas</h5>
                  <ul>
                    <li>Organizational clarity and role definitions</li>
                    <li>Sales governance, meetings, and communication cadence</li>
                    <li>Key performance indicators and performance management</li>
                    <li>Lead generation effectiveness</li>
                    <li>CRM usage and value creation</li>
                    <li>Customer feedback and satisfaction</li>
                  </ul>
                  <h5>Approach</h5>
                  <ul className='mb-0'>
                    <li>Interviews with leadership and sales team</li>
                    <li>Participation in sales and cross-functional meetings</li>
                    <li>Selective customer interviews</li>
                    <li>Clear “as-is” assessment with prioritized recommendations</li>
                  </ul>
                </>
              }
            />
          </Col>
        </Row>
        <Row className="g-4 align-items-start mt-1">
          <Col md={6} lg={4}>
            <ServiceCard
              title="Pricing Audit and Upside Potential"
              body="I identify where value is being left on the table and define practical actions to improve pricing discipline, margin, and realization."
              details="I identify where value is being left on the table and define practical actions to improve pricing discipline, margin, and realization."
            />
          </Col>
          <Col md={6} lg={4}>
            <ServiceCard
              title="Team Capability Assessment"
              body="I assess whether your team has the right mix of skills, capabilities, and behaviors to execute your strategy, followed by targeted development recommendations."
              details="I assess whether your team has the right mix of skills, capabilities, and behaviors to execute your strategy, followed by targeted development recommendations."
            />
          </Col>
          <Col md={6} lg={4}>
            <ServiceCard
              title="Strategy Audit"
              body="I objectively assess your current commercial strategy, challenge assumptions, validate customer value propositions, and define opportunities for accelerated growth."
              details="I objectively assess your current commercial strategy, challenge assumptions, validate customer value propositions, and define opportunities for accelerated growth."
            />
          </Col>
          <Col md={6} lg={4}>
            <ServiceCard
              title="Strategy Development and Execution"
              body="I help design or refine your strategy and ensure it is executed through clear plans, ownership, timelines, and regular follow-up. Strategy only matters if it changes results."
              details="I help design or refine your strategy and ensure it is executed through clear plans, ownership, timelines, and regular follow-up. Strategy only matters if it changes results."
            />
          </Col>
          <Col md={6} lg={4}>
            <ServiceCard
              title="Sales Coaching and Mentoring"
              body="Individual and team coaching focused on practical skill improvement."
              details={
                <>
                  <div>Individual and team coaching focused on practical skill improvement, including:</div>
                  <ul className='mb-0'>
                    <li>Value-based selling</li>
                    <li>Customer preparation and storytelling</li>
                    <li>Pricing and margin awareness</li>
                    <li>Financial acumen for sales</li>
                    <li>Forecasting and pipeline management</li>
                  </ul>
                </>
              }
            />
          </Col>
          <Col md={6} lg={4}>
            <ServiceCard
              title="Sales Incentive and Bonus Design"
              body="I design incentive structures that drive the right behaviors, avoid internal friction, and support profitable growth."
              details="I design incentive structures that drive the right behaviors, avoid internal friction, and support profitable growth."
            />
          </Col>
          <Col md={6} lg={4}>
            <ServiceCard
              title="Sales Team Recruitment"
              body="Support from role definition through candidate shortlisting and final recommendation."
              details="Support from role definition through candidate shortlisting and final recommendation."
            />
          </Col>
          <Col md={6} lg={4}>
            <ServiceCard
              title="M&A and Exit Support"
              body="Support for bolt-on acquisitions, integration, or preparation for sale from a commercial and execution perspective."
              details="Support for bolt-on acquisitions, integration, or preparation for sale from a commercial and execution perspective."
            />
          </Col>
          <Col md={6} lg={4}>
            <ServiceCard
              title="Ad-hoc Support"
              body="Focused help for urgent commercial challenges."
              details={
                <>
                  <ul>
                    <li>Issues with forecast accuracy</li>
                    <li>Customer complaints requiring objective external assessment</li>
                    <li>Declining key accounts needing structured recovery plans</li>
                    <li>Dealer and agent channel optimization to improve incentives and margins</li>
                  </ul>
                  <div>Let me know where your challenges are, and I can lead your team to overcome them.</div>
                </>
              }
            />
          </Col>
        </Row>
      </Section>
    </>
  );
};

export default Services;
